import React, { useCallback, useEffect, useRef, useState } from "react";
import { BookOpen, Search, Play, Pause, ChevronLeft, RefreshCw, AlertCircle, Bookmark } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { SectionLayout } from "@/components/SectionLayout";

const API = "https://api.alquran.cloud/v1";
const QURAN_COM = "https://api.quran.com/api/v4";
// Established per-language Qur'an translations (reputable, licensed editions).
const TRANSLATION_EDITION = { en: "en.sahih", fr: "fr.hamidullah", sw: "sw.barwani" };
const RECITERS = [
  { id: "ar.alafasy", name: "Mishary Alafasy", everyayah: "Alafasy_128kbps" },
  { id: "ar.abdurrahmaansudais", name: "Abdurrahman As-Sudais", everyayah: null },
  { id: "ar.saoodshuraym", name: "Saud Ash-Shuraim", everyayah: "Saood_ash-Shuraym_128kbps" },
  { id: "ar.mahermuaiqly", name: "Maher Al Muaiqly", everyayah: null },
  { id: "ar.husary", name: "Mahmoud Al-Husary", everyayah: "Husary_128kbps" },
];
const LAST_KEY = "maqam_quran_last";
const RECITER_KEY = "maqam_quran_reciter";
// Uthmani Bismillah is prepended to ayah 1 of most surahs in the text edition but NOT in the
// transliteration/translation/audio. Strip it so every ayah stays in sync. Done with a
// diacritic-insensitive letter walk because code points (alef wasla, superscript alef, etc.) vary.
const COMBINING_RE = /[\u0610-\u061A\u064B-\u065F\u0670\u06D6-\u06ED\u0640\uFEFF]/;
const BISMILLAH_BASE = "بسماللهالرحمنالرحيم"; // base letters, no spaces/diacritics, alef normalised
const normAlef = (ch) => ("\u0622\u0623\u0625\u0627\u0671".includes(ch) ? "\u0627" : ch);

const OPENING = {
  bismillah: {
    arabic: "بِسْمِ اللَّهِ الرَّحْمَٰنِ الرَّحِيمِ",
    translit: "Bismillahir-Rahmanir-Rahim",
    translation: {
      en: "In the name of Allah, the Most Gracious, the Most Merciful.",
      fr: "Au nom d'Allah, le Tout Miséricordieux, le Très Miséricordieux.",
    },
  },
};

const stripBismillah = (text, surahNum, numberInSurah) => {
  if (surahNum === 1 || numberInSurah !== 1 || !text) return text;
  let i = 0, bi = 0;
  while (i < text.length && bi < BISMILLAH_BASE.length) {
    const ch = text[i];
    if (COMBINING_RE.test(ch) || ch === " " || ch === "\u00A0") { i++; continue; }
    if (normAlef(ch) === BISMILLAH_BASE[bi]) { bi++; i++; } else break;
  }
  if (bi === BISMILLAH_BASE.length) {
    while (i < text.length && (COMBINING_RE.test(text[i]) || text[i] === " " || text[i] === "\u00A0")) i++;
    return text.slice(i);
  }
  return text;
};

const readLast = () => { try { return JSON.parse(localStorage.getItem(LAST_KEY)); } catch { return null; } };

export const QuranReader = () => {
  const { t } = useLang();
  const [surahs, setSurahs] = useState([]);
  const [tab, setTab] = useState("surah");
  const [query, setQuery] = useState("");
  const [listError, setListError] = useState(false);
  const [open, setOpen] = useState(null); // {type, number, name}
  const [startIndex, setStartIndex] = useState(0);
  const [reciter, setReciter] = useState(() => localStorage.getItem(RECITER_KEY) || "ar.alafasy");

  const loadList = useCallback(async () => {
    setListError(false);
    try {
      const r = await fetch(`${API}/surah`);
      const j = await r.json();
      setSurahs(j.data || []);
    } catch {
      setListError(true);
    }
  }, []);

  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { localStorage.setItem(RECITER_KEY, reciter); }, [reciter]);

  // Open from the list => ALWAYS start at the beginning (index 0), reset saved position for this surah.
  const openFromList = (item) => {
    localStorage.setItem(LAST_KEY, JSON.stringify({ ...item, ayahIndex: 0 }));
    setStartIndex(0);
    setOpen(item);
  };

  // Resume => continue at the saved ayah position.
  const resume = () => {
    const last = readLast();
    if (!last) return;
    setStartIndex(last.ayahIndex || 0);
    setOpen({ type: last.type, number: last.number, name: last.name });
  };

  if (open) {
    return (
      <ReadingView
        key={`${open.type}-${open.number}`}
        item={open}
        startIndex={startIndex}
        reciter={reciter}
        setReciter={setReciter}
        onBack={() => setOpen(null)}
      />
    );
  }

  const last = readLast();
  const q = query.trim().toLowerCase();
  const filtered = surahs.filter((s) =>
    !q || s.englishName.toLowerCase().includes(q) || s.englishNameTranslation.toLowerCase().includes(q) || String(s.number) === q
  );

  return (
    <SectionLayout id="quran" icon={BookOpen}>
      {last && (
        <button onClick={resume} data-testid="quran-resume"
          className="mb-5 flex w-full items-center gap-3 rounded-2xl bg-[#0A192F] p-4 text-left transition-transform active:scale-[0.99]">
          <Bookmark size={18} className="shrink-0 text-[#E9CE7D]" />
          <div className="min-w-0 flex-1">
            <p className="text-[11px] uppercase tracking-wide text-white/50">{t("quran.resume")}</p>
            <p className="truncate font-display text-sm font-bold text-white">{last.name}</p>
          </div>
          <Play size={16} className="text-[#E9CE7D]" />
        </button>
      )}

      <div className="mb-4 flex rounded-full bg-slate-100 p-0.5" data-testid="quran-tabs">
        {["surah", "juz"].map((tb) => (
          <button key={tb} onClick={() => setTab(tb)} data-testid={`quran-tab-${tb}`}
            className={`flex-1 rounded-full px-3 py-2 text-sm font-bold transition-colors ${tab === tb ? "bg-[#0A192F] text-white" : "text-slate-500"}`}>
            {t(`quran.${tb}`)}
          </button>
        ))}
      </div>

      {tab === "surah" ? (
        <>
          <div className="relative mb-4">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={query} onChange={(e) => setQuery(e.target.value)} data-testid="quran-search"
              placeholder={t("quran.search")}
              className="w-full rounded-full border border-slate-200 bg-white py-3 pl-11 pr-4 text-sm text-[#0A192F] outline-none focus:border-[#B9D0EC]" />
          </div>

          {listError ? <ErrorBox onRetry={loadList} /> : surahs.length === 0 ? <ListSkeleton /> : (
            <div className="space-y-2.5" data-testid="quran-surah-list">
              {filtered.map((s) => (
                <button key={s.number}
                  onClick={() => openFromList({ type: "surah", number: s.number, name: `${s.number}. ${s.englishName}` })}
                  data-testid={`quran-surah-${s.number}`}
                  className="group flex w-full items-center gap-3.5 rounded-2xl border border-slate-200 bg-white p-3.5 text-left transition-all hover:border-[#B9D0EC] active:scale-[0.99]">
                  <span className="grid h-10 w-10 shrink-0 rotate-45 place-items-center rounded-xl border-2 border-[#0A192F]/15">
                    <span className="-rotate-45 font-display text-sm font-bold text-[#0A192F]">{s.number}</span>
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="font-display text-[15px] font-bold text-[#0A192F]">{s.englishName}</p>
                    <p className="text-xs text-slate-500">
                      {s.englishNameTranslation} · {s.numberOfAyahs} {t("quran.ayahs")} · {s.revelationType === "Meccan" ? t("quran.meccan") : t("quran.medinan")}
                    </p>
                  </div>
                  <span className="font-arabic text-lg text-[#0A192F]">{s.name}</span>
                </button>
              ))}
            </div>
          )}
        </>
      ) : (
        <div className="grid grid-cols-3 gap-2.5" data-testid="quran-juz-list">
          {Array.from({ length: 30 }).map((_, i) => (
            <button key={i} onClick={() => openFromList({ type: "juz", number: i + 1, name: `${t("quran.juzLabel")} ${i + 1}` })}
              data-testid={`quran-juz-${i + 1}`}
              className="rounded-2xl border border-slate-200 bg-white py-5 text-center transition-all hover:border-[#B9D0EC] active:scale-95">
              <p className="text-[11px] uppercase tracking-wide text-slate-400">{t("quran.juzLabel")}</p>
              <p className="font-display text-xl font-bold text-[#0A192F]">{i + 1}</p>
            </button>
          ))}
        </div>
      )}
    </SectionLayout>
  );
};

const ListSkeleton = () => (
  <div className="space-y-2.5">{Array.from({ length: 8 }).map((_, i) => <div key={i} className="h-16 animate-pulse rounded-2xl bg-slate-100" />)}</div>
);

const ErrorBox = ({ onRetry }) => {
  const { t } = useLang();
  return (
    <div className="rounded-2xl bg-white p-8 text-center" data-testid="quran-error">
      <AlertCircle size={22} className="mx-auto mb-2 text-[#B42318]" />
      <p className="mx-auto max-w-xs text-sm text-slate-500">{t("quran.error")}</p>
      <button onClick={onRetry} data-testid="quran-retry" className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-[#0A192F] px-4 py-2 text-xs font-bold text-white active:scale-95">
        <RefreshCw size={13} /> {t("quran.retry")}
      </button>
    </div>
  );
};

const ReadingView = ({ item, startIndex, reciter, setReciter, onBack }) => {
  const { t, L, lang } = useLang();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(false);
  const [current, setCurrent] = useState(-1); // index into tracks
  const [autoAll, setAutoAll] = useState(false);
  const audioRef = useRef(null);
  const tracksRef = useRef([]);
  const scrolledRef = useRef(false);
  const saveTimer = useRef(0);

  const load = useCallback(async () => {
    setError(false);
    setRows(null);
    try {
      const transEd = TRANSLATION_EDITION[lang] || "en.sahih";
      // Scholarly word-by-word transliteration (distinguishes dh/th/ḥ/ṣ/ etc.) from Quran.com.
      const translitBase = item.type === "surah" ? `by_chapter/${item.number}` : `by_juz/${item.number}`;
      const fetchTranslitPage = (p) =>
        fetch(`${QURAN_COM}/verses/${translitBase}?words=true&word_fields=transliteration&per_page=50&page=${p}`).then((r) => r.json());

      const [aqArabic, aqTrans, aqAudio, tlFirst] = await Promise.all([
        fetch(`${API}/${item.type}/${item.number}/quran-uthmani`).then((r) => { if (!r.ok) throw new Error(); return r.json(); }),
        fetch(`${API}/${item.type}/${item.number}/${transEd}`).then((r) => { if (!r.ok) throw new Error(); return r.json(); }),
        fetch(`${API}/${item.type}/${item.number}/${reciter}`).then((r) => { if (!r.ok) throw new Error(); return r.json(); }),
        fetchTranslitPage(1),
      ]);

      const translit = {};
      const collect = (j) => (j.verses || []).forEach((v) => {
        translit[v.verse_key] = v.words.map((w) => (w.transliteration && w.transliteration.text) || "").filter(Boolean).join(" ");
      });
      collect(tlFirst);
      const totalPages = tlFirst.pagination ? tlFirst.pagination.total_pages : 1;
      if (totalPages > 1) {
        const rest = await Promise.all(Array.from({ length: totalPages - 1 }, (_, i) => fetchTranslitPage(i + 2)));
        rest.forEach(collect);
      }

      const ar = aqArabic?.data?.ayahs || [];
      const tn = aqTrans?.data?.ayahs || [];
      const au = aqAudio?.data?.ayahs || [];
      setRows(ar.map((a, i) => {
        const surahNum = a.surah?.number || (item.type === "surah" ? item.number : undefined);
        const key = `${surahNum}:${a.numberInSurah}`;
        return {
          key,
          num: a.numberInSurah,
          surah: a.surah?.englishName,
          arabic: stripBismillah(a.text, surahNum, a.numberInSurah),
          translit: translit[key] || "",
          trans: tn[i]?.text || "",
          audio: au[i]?.audio || "",
        };
      }));
    } catch {
      setError(true);
    }
  }, [item, reciter, lang]);

  useEffect(() => { load(); }, [load]);

  // Fresh mount (keyed per surah) => always start at the top, unless resuming to a saved ayah.
  useEffect(() => {
    if (!rows || scrolledRef.current) return;
    scrolledRef.current = true;
    if (startIndex > 0 && rows[startIndex]) {
      setTimeout(() => {
        document.querySelector(`[data-testid="quran-ayah-${rows[startIndex].key}"]`)?.scrollIntoView({ block: "start" });
      }, 80);
    } else {
      window.scrollTo(0, 0);
    }
  }, [rows, startIndex]);

  // Persist reading position (ayah index) as the user scrolls.
  useEffect(() => {
    const onScroll = () => {
      if (saveTimer.current) return;
      saveTimer.current = window.setTimeout(() => {
        saveTimer.current = 0;
        const els = document.querySelectorAll("[data-ayah-index]");
        let idx = 0;
        for (const el of els) {
          if (el.getBoundingClientRect().top <= 110) idx = parseInt(el.getAttribute("data-ayah-index"), 10);
          else break;
        }
        localStorage.setItem(LAST_KEY, JSON.stringify({ type: item.type, number: item.number, name: item.name, ayahIndex: idx }));
      }, 450);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => { window.removeEventListener("scroll", onScroll); if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, [item]);

  // Build opening (Bismillah where applicable) + ayah audio tracks. The Bismillah recitation clip
  // itself naturally begins with the isti'adha, so no separate isti'adha track is needed.
  const openingItems = [];
  if (rows) {
    if (item.type === "surah" && item.number !== 1 && item.number !== 9) {
      const base = rows[0]?.audio;
      const bsrc = base ? base.replace(/\/\d+\.mp3$/, "/1.mp3") : `https://cdn.islamic.network/quran/audio/128/${reciter}/1.mp3`;
      openingItems.push({ id: "bismillah", kind: "bismillah", src: bsrc });
    }
  }
  tracksRef.current = rows
    ? [...openingItems.map((o) => ({ src: o.src, hlid: o.id })),
       ...rows.map((r, i) => ({ src: r.audio, hlid: `ayah-${i}` }))]
    : [];

  const stop = () => { audioRef.current?.pause(); setCurrent(-1); setAutoAll(false); };

  const playTrack = (ti, auto) => {
    const tr = tracksRef.current[ti];
    if (!tr || !tr.src) { // skip tracks without audio
      if (auto && ti + 1 < tracksRef.current.length) playTrack(ti + 1, true);
      return;
    }
    const a = audioRef.current;
    a.src = tr.src;
    a.play().catch(() => {});
    setCurrent(ti);
    setAutoAll(!!auto);
  };

  const onEnded = () => {
    if (autoAll && current + 1 < tracksRef.current.length) playTrack(current + 1, true);
    else { setCurrent(-1); setAutoAll(false); }
  };

  const togglePlayAll = () => { if (current >= 0) stop(); else playTrack(0, true); };

  const currentHlid = current >= 0 ? tracksRef.current[current]?.hlid : null;
  const playAyah = (i) => {
    const ti = openingItems.length + i;
    currentHlid === `ayah-${i}` ? stop() : playTrack(ti, false);
  };
  const playOpening = (id) => {
    const ti = openingItems.findIndex((o) => o.id === id);
    currentHlid === id ? stop() : playTrack(ti, false);
  };

  const OpeningCard = ({ id }) => {
    const data = OPENING[id];
    const on = currentHlid === id;
    return (
      <div data-testid={`quran-opening-${id}`}
        className={`rounded-3xl border p-5 transition-colors ${on ? "border-[#B9D0EC] bg-[#EAF2FC]" : "border-dashed border-slate-300 bg-white"}`}>
        <div className="mb-3 flex items-center justify-between">
          <span className="rounded-full bg-[#D4AF37]/15 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-[#8a6d1b]">
            {id === "istiadha" ? "Isti'adha" : "Bismillah"}
          </span>
          <button onClick={() => playOpening(id)} data-testid={`quran-opening-play-${id}`}
            className="grid h-8 w-8 place-items-center rounded-full bg-slate-100 text-[#0A192F] active:scale-90">
            {on ? <Pause size={15} /> : <Play size={15} />}
          </button>
        </div>
        <p dir="rtl" lang="ar" className="font-arabic text-[26px] leading-[2] text-[#0A192F]">{data.arabic}</p>
        <p className="mt-3 text-sm font-semibold italic text-[#3E6B9A]">{data.translit}</p>
        <p className="mt-2 text-sm leading-relaxed text-slate-600">{L(data.translation)}</p>
      </div>
    );
  };

  return (
    <div className="min-h-[100svh] bg-[#F8FAFC]" data-testid="quran-reading">
      <audio ref={audioRef} onEnded={onEnded} />
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/92 backdrop-blur-xl">
        <div className="mx-auto flex w-full max-w-xl items-center justify-between gap-2 px-3 py-2.5 sm:px-6">
          <button onClick={() => { stop(); onBack(); }} data-testid="quran-reading-back" className="inline-flex items-center gap-1 rounded-full py-1.5 pl-1.5 pr-3 text-sm font-semibold text-[#0A192F] hover:bg-slate-100 active:scale-95">
            <ChevronLeft size={20} strokeWidth={2.25} /> {t("back")}
          </button>
          <span className="truncate font-display text-sm font-bold text-[#0A192F]">{item.name}</span>
          <button onClick={togglePlayAll} data-testid="quran-play-all" className="inline-flex items-center gap-1.5 rounded-full bg-[#0A192F] px-3 py-1.5 text-xs font-bold text-white active:scale-95">
            {current >= 0 ? <Pause size={14} /> : <Play size={14} />}
            {current >= 0 ? t("quran.pause") : t("quran.playSurah")}
          </button>
        </div>
        <div className="mx-auto flex w-full max-w-xl items-center gap-2 px-3 pb-2.5 sm:px-6">
          <span className="text-[11px] font-semibold text-slate-400">{t("quran.reciter")}:</span>
          <select value={reciter} onChange={(e) => { stop(); setReciter(e.target.value); }} data-testid="quran-reciter-select"
            className="flex-1 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-[#0A192F] outline-none">
            {RECITERS.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
        </div>
      </header>

      <div className="mx-auto w-full max-w-xl px-5 pb-28 pt-6 sm:px-6">
        {error ? <ErrorBox onRetry={load} /> : !rows ? (
          <div className="space-y-3">{Array.from({ length: 5 }).map((_, i) => <div key={i} className="h-28 animate-pulse rounded-2xl bg-slate-100" />)}</div>
        ) : (
          <div className="space-y-3.5">
            {openingItems.map((o) => <OpeningCard key={o.id} id={o.id} />)}
            {rows.map((a, i) => (
              <div key={a.key} data-testid={`quran-ayah-${a.key}`} data-ayah-index={i}
                className={`rounded-3xl border p-5 transition-colors ${currentHlid === `ayah-${i}` ? "border-[#B9D0EC] bg-[#EAF2FC]" : "border-slate-200 bg-white"}`}>
                <div className="mb-3 flex items-center justify-between">
                  <span className="grid h-7 min-w-7 place-items-center rounded-full bg-[#0A192F] px-2 text-[11px] font-bold text-[#E9CE7D]">
                    {item.type === "juz" && a.surah ? `${a.surah} ` : ""}{a.num}
                  </span>
                  {a.audio && (
                    <button onClick={() => playAyah(i)} data-testid={`quran-ayah-play-${a.key}`}
                      className="grid h-8 w-8 place-items-center rounded-full bg-slate-100 text-[#0A192F] active:scale-90">
                      {currentHlid === `ayah-${i}` ? <Pause size={15} /> : <Play size={15} />}
                    </button>
                  )}
                </div>
                <p dir="rtl" lang="ar" className="font-arabic text-[26px] leading-[2] text-[#0A192F]">{a.arabic}</p>
                {a.translit && <p className="mt-3 text-sm font-semibold italic text-[#3E6B9A]">{a.translit}</p>}
                {a.trans && <p className="mt-2 text-sm leading-relaxed text-slate-600">{a.trans}</p>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
